<template>
  <v-dialog v-model="selectProductsDialog" max-width="1200" persistent scrollable>
    <template v-slot:activator="{ on }">
      <span v-if="buttonType === 'button'" v-on="on">
        <mf-button label="Selecionar produtos" outlined :color="error ? 'vermelho-d20' : 'marca-0-l10'" :disabled="disabled" />
      </span>
      <span v-else-if="buttonType === 'link'" class="link" style="font-size: 16px !important" v-on="on">Ver {{ productMessage }}</span>
    </template>

    <v-card>
      <v-card-title>{{ viewOnly ? 'Produtos' : 'Selecionar Produtos' }}</v-card-title>
      <v-card-text v-if="isMobile" class="pa-4" style="height: 64vh; overflow-y: scroll">
        <v-tabs v-model="tab" centered icons-and-text color="#00B395">
          <v-tabs-slider></v-tabs-slider>

          <v-tab href="#tab-1" :disabled="viewOnly">
            Pesquisar
            <v-icon>mdi-magnify</v-icon>
          </v-tab>

          <v-tab href="#tab-2">
            Selecionados
            <v-badge :content="chosenProducts.length" :value="chosenProducts.length" color="#125b4f" overlap>
              <v-icon>mdi-tag-check</v-icon>
            </v-badge>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item value="tab-1" class="py-2">
            <mf-search v-model="searchValue" hide-details placeholder="Pesquisar produtos" dense />
            <span v-if="noResults">Nenhum resultado encontrado ou todos os resultados já foram selecionados.</span>
            <mf-toggle v-model="onlyActive" label="Somente produtos ativos" class="ma-0" hide-details dense />
            <mf-toggle v-model="sortByRelevance" label="Ordenar por relevância de venda" class="ma-0" hide-details dense />
            <product-list
              :loading="loading"
              :products="products"
              :no-results="noResults"
              :view-only="viewOnly"
              is-adding
              @addProduct="(product) => addProduct(product)"
              @addAllProducts="addAllProducts"
            />
          </v-tab-item>

          <v-tab-item value="tab-2" class="py-2">
            <product-list
              :loading="loading"
              :products="chosenProducts"
              :view-only="viewOnly"
              :is-adding="false"
              @removeProduct="(product) => removeProduct(product)"
              @removeAllProducts="removeAllProducts"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-text v-else class="pt-4">
        <mf-search v-if="!viewOnly" v-model="searchValue" hide-details placeholder="Pesquisar produtos" />
        <span v-if="noResults">Nenhum resultado encontrado ou todos os resultados já foram selecionados.</span>
        <div class="d-flex flex-row" style="gap: 18px">
          <product-list
            v-if="!viewOnly"
            :v-show="!noResults"
            style="flex: 50%"
            :loading="loading"
            :products="products"
            :no-results="noResults"
            :view-only="viewOnly"
            is-adding
            @addProduct="(product) => addProduct(product)"
            @addAllProducts="addAllProducts"
          />
          <product-list
            style="flex: 50%"
            :loading="loading"
            :products="chosenProducts"
            :view-only="viewOnly"
            :is-adding="false"
            @removeProduct="(product) => removeProduct(product)"
            @removeAllProducts="removeAllProducts"
          />
        </div>
        <mf-toggle v-if="!viewOnly" v-model="onlyActive" label="Somente produtos ativos" class="ma-0" hide-details dense />
        <mf-toggle v-if="!viewOnly" v-model="sortByRelevance" label="Ordenar por relevância de venda" class="ma-0" hide-details dense />
      </v-card-text>

      <mf-action-buttons
        class="px-5 pb-5"
        :primary-button="{
          text: 'Confirmar',
          action: handleConfirm,
          isVisible: !viewOnly,
          isDisabled: disableSave,
        }"
        :secondary-button="{
          text: 'Fechar',
          action: handleClose,
          isVisible: true,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { QUERY_SKU_NAME_PRODUCTS } from '@/modules/produtos/graphql'
import ProductList from './ProductList.vue'

export default {
  components: {
    ProductList,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    disableSave: {
      type: Boolean,
      default: false,
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
    error: {
      type: Boolean,
      default: false,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
    buttonType: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'link'].includes(value),
    },
  },
  data: () => ({
    loading: false,
    selectProductsDialog: false,
    onlyActive: true,
    sortByRelevance: true,
    timeout: null,
    selectedTab: 0,
    tab: null,
    noResults: false,
    searchValue: '',
    products: [],
    chosenProducts: [],
  }),
  computed: {
    productMessage() {
      if (this.selectedProducts.length === 1) return '1 produto selecionado'
      else return `${this.selectedProducts.length} produtos selecionados`
    },
  },
  watch: {
    selectedProducts(newValue) {
      this.chosenProducts = [...newValue]
    },
    sortByRelevance() {
      if (this.searchValue && this.searchValue.length > 2) {
        this.search()
      }
    },
    onlyActive() {
      if (this.searchValue && this.searchValue.length > 2) {
        this.search()
      }
    },
    searchValue(newValue) {
      if (!newValue || newValue.length < 2) return

      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.search()
      }, 500)
    },
    selectProductsDialog(open) {
      if (open) {
        this.$emit('open')
        if (this.chosenProducts && this.chosenProducts.length > 0) this.tab = 'tab-2'
      }
    },
  },
  mounted() {
    this.chosenProducts = [...this.selectedProducts]
  },
  methods: {
    clearData() {
      this.searchValue = ''
      this.products = []
      this.noResults = false
      this.chosenProducts = [...this.selectedProducts]
    },
    handleClose() {
      this.clearData()
      this.selectProductsDialog = !this.selectProductsDialog
    },
    handleConfirm() {
      this.$emit('update:selectedProducts', [...this.chosenProducts])
      this.handleClose()
    },
    async search() {
      this.loading = true
      this.noResults = false
      const variables = {
        search: this.searchValue,
        active: this.onlyActive,
      }
      if (this.sortByRelevance) {
        variables.sort = ['eanFullMatch', 'skuFullMatch', 'active', 'relevance_score', 'name']
        variables.sort_direction = ['DESC', 'DESC', 'DESC', 'DESC', 'ASC']
      }
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_SKU_NAME_PRODUCTS,
          variables,
          fetchPolicy: 'no-cache',
        })

        this.products = this.removeChosenProductsFromResult(data?.products?.products)
        if (this.products.length <= 0) this.noResults = true
      } catch {
        this.$snackbar({ message: 'Erro ao carregar produtos', snackbarColor: 'error' })
        this.noResults = false
      } finally {
        this.loading = false
      }
    },
    removeChosenProductsFromResult(result) {
      for (let item of this.chosenProducts) {
        let itemIndex = result.findIndex((product) => product.sku === item.sku)
        if (itemIndex > -1) {
          result.splice(itemIndex, 1)
        }
      }
      return result
    },
    addProduct(item) {
      const productAlreadyExists = this.chosenProducts.findIndex((product) => product._id === item._id)
      if (productAlreadyExists >= 0) this.$snackbar({ message: 'Produto já selecionado', snackbarColor: 'error' })
      else {
        this.chosenProducts.push(item)
        const productIndex = this.products.findIndex((product) => product._id === item._id)
        if (productIndex >= 0) this.products.splice(productIndex, 1)
      }
    },
    removeProduct(item) {
      if (this.viewOnly) return
      const index = this.chosenProducts.findIndex((product) => product._id === item._id)
      if (index >= 0) {
        this.chosenProducts.splice(index, 1)
        this.products.unshift(item)
      }
    },
    addAllProducts() {
      this.chosenProducts.push(...this.products)
      this.products = []
    },
    removeAllProducts() {
      this.products.unshift(...this.chosenProducts)
      this.chosenProducts = []
    },
  },
}
</script>

<style lang="scss" scoped>
.active-tab {
  color: $marca-1;
}
</style>
